import { ReactNode } from 'react';
import { GqlMenuSection } from 'types/graphql';

type Carousel = {
  type: 'carousel';
  carousel: GqlMenuSection;
};

type Interstitial = {
  type: 'interstitial';
  interstitial: ReactNode;
};

type CarouselSequence = Array<Carousel | Interstitial>;

const createCarousel = (carousel: GqlMenuSection): Carousel => ({
  type: 'carousel',
  carousel,
});

const createInterstitial = (interstitial: ReactNode): Interstitial => ({
  type: 'interstitial',
  interstitial,
});

export const createCarouselSequence = (
  carousels: GqlMenuSection[],
  interstitials: ReactNode[] | null | undefined
): CarouselSequence => {
  if (!carousels.length) {
    return [];
  }

  if (!interstitials?.length) {
    return carousels.map(createCarousel);
  }

  const result: CarouselSequence = [];

  // Case 1: More interstitials than carousels
  // Start with interstitial, alternate, truncate naturally
  if (interstitials.length > carousels.length) {
    result.push(createInterstitial(interstitials[0]));

    for (let i = 0; i < carousels.length; i++) {
      result.push(createCarousel(carousels[i]));

      // Add next interstitial if we have one
      if (i + 1 < interstitials.length) {
        result.push(createInterstitial(interstitials[i + 1]));
      }
    }
  }
  // Case 2: More carousels than interstitials (or equal)
  // Calculate positions where interstitials should be placed
  else {
    const positions: number[] = [];

    // Use equal distribution when carousels divide evenly among multiple interstitials
    if (carousels.length % interstitials.length === 0 && interstitials.length > 1) {
      const carouselsPerInterstitial = carousels.length / interstitials.length;
      for (let i = 0; i < interstitials.length; i++) {
        positions.push(carouselsPerInterstitial * (i + 1));
      }
    } else {
      // Otherwise use sections approach
      const totalSections = interstitials.length + 1;
      const carouselsPerSection = Math.floor(carousels.length / totalSections);
      const extraCarousels = carousels.length % totalSections;

      let currentPosition = 0;
      for (let i = 0; i < interstitials.length; i++) {
        const sectionSize = carouselsPerSection + (i < extraCarousels ? 1 : 0);
        currentPosition += sectionSize;
        positions.push(currentPosition);
      }
    }

    let carouselIndex = 0;
    let interstitialIndex = 0;

    while (carouselIndex < carousels.length) {
      result.push(createCarousel(carousels[carouselIndex]));
      carouselIndex += 1;

      if (interstitialIndex < interstitials.length && carouselIndex === positions[interstitialIndex]) {
        result.push(createInterstitial(interstitials[interstitialIndex]));
        interstitialIndex += 1;
      }
    }

    while (interstitialIndex < interstitials.length) {
      result.push(createInterstitial(interstitials[interstitialIndex]));
      interstitialIndex += 1;
    }
  }

  return result;
};
